"use client";

import {
  assistantBubbleBackgroundVariants,
  assistantBubbleRootVariants,
  userBubbleBackgroundVariants,
  userBubbleRootVariants,
  type AssistantStaticBubbleVariantProps,
  type UserStaticBubbleVariantProps,
} from "@ui/components/core/BaseChat";
import type { Transition } from "motion/react";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { flushSync } from "react-dom";
import { getMessageAlignmentClassName } from "./alignment";
import { ChatScenarioAssistantTypingIndicator, TypingDots } from "./ChatScenarioTypingIndicator";
import { MessageAvatar } from "./MessageAvatar";
import { MessagePartsList } from "./messageParts";
import type { ChatScenarioAvatar, ChatScenarioMessage } from "./types";

const messageEnterTransition: Transition = { type: "spring", stiffness: 300, damping: 25 };
const morphEnterTransition: Transition = { type: "spring", stiffness: 360, damping: 28 };

const sameBubbleEnterTransition: Transition = { duration: 0.22, ease: [0.25, 0.1, 0.25, 1] };
const sameBubbleSwapTransition: Transition = { duration: 0.22, ease: [0.25, 0.1, 0.25, 1] };
// Duration-based ease-out curve (≈ easeOutQuint). Guaranteed monotonic — no overshoot, no rest-snap.
const sameBubbleLayoutTransition: Transition = { duration: 0.42, ease: [0.22, 1, 0.36, 1] };
const sameBubbleSwapInitial = { opacity: 0, filter: "blur(6px)" } as const;
const sameBubbleSwapAnimate = { opacity: 1, filter: "blur(0px)" } as const;
const sameBubbleSwapExit = { opacity: 0, filter: "blur(6px)" } as const;

interface AnimatedScenarioMessageProps {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  message: ChatScenarioMessage;
  messageIndex: number;
  playbackRunId: number;
  onMessageComplete: (messageIndex: number, playbackRunId: number) => void;
  /**
   * When true the message is entering in place of a typing indicator bubble — use a cross-fade
   * instead of the standard slide-up so it visually replaces the dots.
   */
  morphFromTyping?: boolean;
  partDelayMS?: number;
  /** When true with positive `partDelayMS`, shows assistant typing dots between animated assistant parts. */
  typingIndicatorBetweenAssistantParts?: boolean;
  userMessageVariants: UserStaticBubbleVariantProps;
}

interface StaticScenarioMessageProps {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  message: ChatScenarioMessage;
  userMessageVariants: UserStaticBubbleVariantProps;
}

interface MessageWithAvatarProps {
  avatar?: ChatScenarioAvatar;
  role: ChatScenarioMessage["role"];
  children: React.ReactNode;
}

/** Wraps message content with an optional avatar, positioned based on role */
function MessageWithAvatar({ avatar, role, children }: MessageWithAvatarProps): JSX.Element {
  if (!avatar) {
    return <>{children}</>;
  }

  const avatarElement = <MessageAvatar avatar={avatar} />;

  if (role === "user") {
    return (
      <div className='tw-flex tw-items-start tw-gap-2'>
        <div className='tw-min-w-0 tw-flex-1'>{children}</div>
        {avatarElement}
      </div>
    );
  }

  return (
    <div className='tw-flex tw-items-start tw-gap-2'>
      {avatarElement}
      <div className='tw-min-w-0 tw-flex-1'>{children}</div>
    </div>
  );
}

function UserMessage({
  assistantMessageVariants,
  message,
  messageIndex,
  morphFromTyping = false,
  playbackRunId,
  onMessageComplete,
  userMessageVariants,
}: AnimatedScenarioMessageProps): JSX.Element {
  const handledEnterRef = useRef(false);

  useEffect(() => {
    handledEnterRef.current = false;
  }, [playbackRunId, message.id]);

  const handleEnterComplete = useCallback(() => {
    if (handledEnterRef.current) {
      return;
    }
    handledEnterRef.current = true;
    onMessageComplete(messageIndex, playbackRunId);
  }, [messageIndex, onMessageComplete, playbackRunId]);

  return (
    <m.div
      initial={
        morphFromTyping
          ? { opacity: 0, scale: 0.94, transform: "translateY(0rem)" }
          : { opacity: 0, transform: "translateY(0.625rem)", scale: 0.95 }
      }
      animate={{ opacity: 1, transform: "translateY(0rem)", scale: 1 }}
      transition={morphFromTyping ? morphEnterTransition : messageEnterTransition}
      onAnimationComplete={handleEnterComplete}
    >
      <MessageWithAvatar avatar={message.avatar} role={message.role}>
        <MessagePartsList
          messageId={message.id}
          role={message.role}
          parts={message.parts}
          assistantMessageVariants={assistantMessageVariants}
          userMessageVariants={userMessageVariants}
        />
      </MessageWithAvatar>
    </m.div>
  );
}

function AssistantMessage({
  assistantMessageVariants,
  message,
  messageIndex,
  morphFromTyping = false,
  playbackRunId,
  onMessageComplete,
  partDelayMS = 0,
  typingIndicatorBetweenAssistantParts = false,
  userMessageVariants,
}: AnimatedScenarioMessageProps): JSX.Element {
  // When morphing from a typing bubble the container is already fading in with content visible —
  // start with the first part revealed so the text appears during (not after) the morph animation.
  const [visiblePartCount, setVisiblePartCount] = useState(morphFromTyping ? 1 : 0);
  const [partTypingIndicatorVisible, setPartTypingIndicatorVisible] = useState(false);
  const [partTypingPresenceKey, setPartTypingPresenceKey] = useState(0);
  const hasStartedRef = useRef(morphFromTyping);
  const hasCompletedRef = useRef(false);
  const visiblePartCountRef = useRef(morphFromTyping ? 1 : 0);
  const partDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDismissAfterPartShellRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (partDelayTimerRef.current !== null) {
        clearTimeout(partDelayTimerRef.current);
        partDelayTimerRef.current = null;
      }
    };
  }, []);

  const handleComplete = useCallback(() => {
    onMessageComplete(messageIndex, playbackRunId);
  }, [messageIndex, onMessageComplete, playbackRunId]);

  const handleAnimatedPartShellComplete = useCallback((partIndex: number) => {
    if (pendingDismissAfterPartShellRef.current !== partIndex) {
      return;
    }
    pendingDismissAfterPartShellRef.current = null;
    setPartTypingIndicatorVisible(false);
  }, []);

  const revealParts = useCallback((count: number) => {
    visiblePartCountRef.current = count;
    setVisiblePartCount(count);
  }, []);

  const handleMessageEnterComplete = useCallback(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (message.parts.length === 0) {
      hasCompletedRef.current = true;
      handleComplete();
      return;
    }

    revealParts(1);
  }, [handleComplete, message.parts.length, revealParts]);

  const handlePartComplete = useCallback(
    (partIndex: number) => {
      if (hasCompletedRef.current || visiblePartCountRef.current !== partIndex + 1) {
        return;
      }

      const nextVisiblePartCount = partIndex + 2;

      if (nextVisiblePartCount > message.parts.length) {
        hasCompletedRef.current = true;
        handleComplete();
        return;
      }

      const nextPart = message.parts[nextVisiblePartCount - 1];
      const nextPartIsSpeech = nextPart?.type === "speech";
      const showPartTyping = typingIndicatorBetweenAssistantParts && partDelayMS > 0 && !nextPartIsSpeech;

      if (showPartTyping) {
        setPartTypingPresenceKey(k => k + 1);
        setPartTypingIndicatorVisible(true);
      }

      partDelayTimerRef.current = setTimeout(() => {
        partDelayTimerRef.current = null;
        pendingDismissAfterPartShellRef.current = showPartTyping ? nextVisiblePartCount - 1 : null;

        flushSync(() => {
          revealParts(nextVisiblePartCount);
        });

        if (!showPartTyping) {
          setPartTypingIndicatorVisible(false);
        }
      }, partDelayMS);
    },
    [handleComplete, message.parts.length, partDelayMS, revealParts, typingIndicatorBetweenAssistantParts],
  );

  return (
    <m.div
      initial={
        morphFromTyping
          ? { opacity: 0, scale: 0.94, transform: "translateY(0rem)" }
          : { opacity: 0, transform: "translateY(0.625rem)", scale: 0.95 }
      }
      animate={{ opacity: 1, transform: "translateY(0rem)", scale: 1 }}
      transition={morphFromTyping ? morphEnterTransition : messageEnterTransition}
      onAnimationComplete={handleMessageEnterComplete}
    >
      <MessageWithAvatar avatar={message.avatar} role={message.role}>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <MessagePartsList
            messageId={message.id}
            role={message.role}
            parts={message.parts}
            visiblePartCount={visiblePartCount}
            animateParts
            onPartComplete={handlePartComplete}
            onAnimatedPartShellComplete={
              typingIndicatorBetweenAssistantParts ? handleAnimatedPartShellComplete : undefined
            }
            morphFirstPart={morphFromTyping}
            assistantMessageVariants={assistantMessageVariants}
            userMessageVariants={userMessageVariants}
          />
          <AnimatePresence initial={false} mode='popLayout'>
            {typingIndicatorBetweenAssistantParts && partTypingIndicatorVisible ? (
              <m.div
                key={`${playbackRunId}-${messageIndex}-assistant-part-typing-${partTypingPresenceKey}`}
                layout='position'
                exit={{
                  opacity: 0,
                  scale: 0.94,
                  transition: { duration: 0.18, ease: [0.25, 0.1, 0.25, 1] },
                }}
                transition={{
                  layout: { type: "spring", stiffness: 380, damping: 34 },
                }}
              >
                <ChatScenarioAssistantTypingIndicator assistantMessageVariants={assistantMessageVariants} />
              </m.div>
            ) : null}
          </AnimatePresence>
        </div>
      </MessageWithAvatar>
    </m.div>
  );
}

export const UserScenarioMessage = memo(UserMessage);
export const AssistantScenarioMessage = memo(AssistantMessage);

interface SameBubbleScenarioMessageProps {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  message: ChatScenarioMessage;
  messageIndex: number;
  playbackRunId: number;
  onMessageComplete: (messageIndex: number, playbackRunId: number) => void;
  loadingDurationMs: number;
  partDelayMS?: number;
  typingIndicatorBetweenAssistantParts?: boolean;
  userMessageVariants: UserStaticBubbleVariantProps;
}

function SameBubbleUserMessage({
  assistantMessageVariants,
  message,
  messageIndex,
  playbackRunId,
  onMessageComplete,
  loadingDurationMs,
  userMessageVariants,
}: SameBubbleScenarioMessageProps): JSX.Element {
  const textContent = message.parts.find(part => part.type === "text" || part.type === "speech");
  const canMorph = textContent?.type === "text";
  const initialPhase: "loading" | "content" = canMorph && loadingDurationMs > 0 ? "loading" : "content";
  const [phase, setPhase] = useState<"loading" | "content">(initialPhase);
  const handledRef = useRef(false);

  useEffect(() => {
    handledRef.current = false;
  }, [playbackRunId, message.id]);

  useEffect(() => {
    if (phase === "content" || !canMorph || loadingDurationMs <= 0) {
      return;
    }
    const id = setTimeout(() => setPhase("content"), loadingDurationMs);
    return () => clearTimeout(id);
  }, [phase, canMorph, loadingDurationMs]);

  const handleContentSettled = useCallback(() => {
    if (handledRef.current) {
      return;
    }
    handledRef.current = true;
    onMessageComplete(messageIndex, playbackRunId);
  }, [messageIndex, onMessageComplete, playbackRunId]);

  if (!canMorph) {
    return (
      <UserMessage
        assistantMessageVariants={assistantMessageVariants}
        message={message}
        messageIndex={messageIndex}
        playbackRunId={playbackRunId}
        onMessageComplete={onMessageComplete}
        userMessageVariants={userMessageVariants}
      />
    );
  }

  return (
    <MessageWithAvatar avatar={message.avatar} role={message.role}>
      <m.div
        layout
        className={`${userBubbleRootVariants(userMessageVariants)} tw-flex tw-min-h-10 tw-items-center tw-overflow-hidden`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ default: sameBubbleEnterTransition, layout: sameBubbleLayoutTransition }}
        style={{ transformOrigin: "bottom right" }}
      >
        <div aria-hidden className={userBubbleBackgroundVariants(userMessageVariants)} />
        <AnimatePresence mode='popLayout' initial={false}>
          {phase === "loading" ? (
            <m.span
              key='loading'
              className='tw-block'
              initial={sameBubbleSwapInitial}
              animate={sameBubbleSwapAnimate}
              exit={sameBubbleSwapExit}
              transition={sameBubbleSwapTransition}
              aria-hidden
            >
              <TypingDots />
            </m.span>
          ) : (
            <m.span
              key='content'
              className='tw-block'
              initial={sameBubbleSwapInitial}
              animate={sameBubbleSwapAnimate}
              exit={sameBubbleSwapExit}
              transition={sameBubbleSwapTransition}
              onAnimationComplete={handleContentSettled}
            >
              {textContent.content}
            </m.span>
          )}
        </AnimatePresence>
      </m.div>
    </MessageWithAvatar>
  );
}

function SameBubbleAssistantMessage({
  assistantMessageVariants,
  message,
  messageIndex,
  playbackRunId,
  onMessageComplete,
  loadingDurationMs,
  partDelayMS,
  typingIndicatorBetweenAssistantParts,
  userMessageVariants,
}: SameBubbleScenarioMessageProps): JSX.Element {
  const firstPart = message.parts[0];
  const canMorph = firstPart?.type === "text";
  const restParts = message.parts.slice(1);
  const initialPhase: "loading" | "content" = canMorph && loadingDurationMs > 0 ? "loading" : "content";
  const [phase, setPhase] = useState<"loading" | "content">(initialPhase);
  const [visibleRestPartCount, setVisibleRestPartCount] = useState(0);
  const [partTypingVisible, setPartTypingVisible] = useState(false);
  const [partTypingKey, setPartTypingKey] = useState(0);
  const visibleRestPartCountRef = useRef(0);
  const firstPartSettledRef = useRef(false);
  const completedRef = useRef(false);
  const partDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    firstPartSettledRef.current = false;
    completedRef.current = false;
    visibleRestPartCountRef.current = 0;
    setVisibleRestPartCount(0);
    setPartTypingVisible(false);
  }, [playbackRunId, message.id]);

  useEffect(() => {
    return () => {
      if (partDelayTimerRef.current !== null) {
        clearTimeout(partDelayTimerRef.current);
        partDelayTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (phase === "content" || loadingDurationMs <= 0) {
      return;
    }
    const id = setTimeout(() => setPhase("content"), loadingDurationMs);
    return () => clearTimeout(id);
  }, [phase, loadingDurationMs]);

  const completeMessage = useCallback(() => {
    if (completedRef.current) {
      return;
    }
    completedRef.current = true;
    onMessageComplete(messageIndex, playbackRunId);
  }, [messageIndex, onMessageComplete, playbackRunId]);

  const revealNextRestPart = useCallback((nextCount: number) => {
    visibleRestPartCountRef.current = nextCount;
    setVisibleRestPartCount(nextCount);
  }, []);

  const handleFirstPartSettled = useCallback(() => {
    if (firstPartSettledRef.current) {
      return;
    }
    firstPartSettledRef.current = true;

    if (restParts.length === 0) {
      completeMessage();
      return;
    }

    const nextRestIndex = 0;
    const nextPart = restParts[nextRestIndex];
    const nextPartIsSpeech = nextPart?.type === "speech";
    const showPartTyping = Boolean(typingIndicatorBetweenAssistantParts) && (partDelayMS ?? 0) > 0 && !nextPartIsSpeech;

    if (showPartTyping) {
      setPartTypingKey(k => k + 1);
      setPartTypingVisible(true);
    }

    partDelayTimerRef.current = setTimeout(() => {
      partDelayTimerRef.current = null;
      flushSync(() => {
        revealNextRestPart(1);
      });
      if (!showPartTyping) {
        setPartTypingVisible(false);
      }
    }, partDelayMS ?? 0);
  }, [completeMessage, partDelayMS, restParts, revealNextRestPart, typingIndicatorBetweenAssistantParts]);

  const handleRestPartComplete = useCallback(
    (partIndex: number) => {
      if (completedRef.current || visibleRestPartCountRef.current !== partIndex + 1) {
        return;
      }

      const nextVisibleCount = partIndex + 2;

      if (nextVisibleCount > restParts.length) {
        completeMessage();
        return;
      }

      const nextPart = restParts[nextVisibleCount - 1];
      const nextPartIsSpeech = nextPart?.type === "speech";
      const showPartTyping =
        Boolean(typingIndicatorBetweenAssistantParts) && (partDelayMS ?? 0) > 0 && !nextPartIsSpeech;

      if (showPartTyping) {
        setPartTypingKey(k => k + 1);
        setPartTypingVisible(true);
      }

      partDelayTimerRef.current = setTimeout(() => {
        partDelayTimerRef.current = null;
        flushSync(() => {
          revealNextRestPart(nextVisibleCount);
        });
        if (!showPartTyping) {
          setPartTypingVisible(false);
        }
      }, partDelayMS ?? 0);
    },
    [completeMessage, partDelayMS, restParts, revealNextRestPart, typingIndicatorBetweenAssistantParts],
  );

  const handleAnimatedPartShellComplete = useCallback((partIndex: number) => {
    if (visibleRestPartCountRef.current !== partIndex + 1) {
      return;
    }
    setPartTypingVisible(false);
  }, []);

  if (!canMorph || firstPart?.type !== "text") {
    return (
      <AssistantMessage
        assistantMessageVariants={assistantMessageVariants}
        message={message}
        messageIndex={messageIndex}
        playbackRunId={playbackRunId}
        onMessageComplete={onMessageComplete}
        partDelayMS={partDelayMS}
        typingIndicatorBetweenAssistantParts={typingIndicatorBetweenAssistantParts}
        userMessageVariants={userMessageVariants}
      />
    );
  }

  return (
    <MessageWithAvatar avatar={message.avatar} role={message.role}>
      <div className='tw-flex tw-flex-col tw-gap-2'>
        <m.div
          layout
          className={`${assistantBubbleRootVariants(assistantMessageVariants)} tw-flex tw-min-h-10 tw-items-center tw-overflow-hidden`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ default: sameBubbleEnterTransition, layout: sameBubbleLayoutTransition }}
          style={{ transformOrigin: "bottom left" }}
        >
          <div aria-hidden className={assistantBubbleBackgroundVariants(assistantMessageVariants)} />
          <AnimatePresence mode='popLayout' initial={false}>
            {phase === "loading" ? (
              <m.span
                key='loading'
                className='tw-block'
                initial={sameBubbleSwapInitial}
                animate={sameBubbleSwapAnimate}
                exit={sameBubbleSwapExit}
                transition={sameBubbleSwapTransition}
                aria-hidden
              >
                <TypingDots />
              </m.span>
            ) : (
              <m.span
                key='content'
                className='tw-block'
                initial={sameBubbleSwapInitial}
                animate={sameBubbleSwapAnimate}
                exit={sameBubbleSwapExit}
                transition={sameBubbleSwapTransition}
                onAnimationComplete={handleFirstPartSettled}
              >
                {firstPart.content}
              </m.span>
            )}
          </AnimatePresence>
        </m.div>

        {restParts.length > 0 && phase === "content" && (
          <MessagePartsList
            messageId={`${message.id ?? messageIndex}-rest`}
            role={message.role}
            parts={restParts}
            visiblePartCount={visibleRestPartCount}
            animateParts
            onPartComplete={handleRestPartComplete}
            onAnimatedPartShellComplete={
              typingIndicatorBetweenAssistantParts ? handleAnimatedPartShellComplete : undefined
            }
            assistantMessageVariants={assistantMessageVariants}
            userMessageVariants={userMessageVariants}
          />
        )}

        <AnimatePresence initial={false} mode='popLayout'>
          {typingIndicatorBetweenAssistantParts && partTypingVisible ? (
            <m.div
              key={`${playbackRunId}-${messageIndex}-same-bubble-rest-typing-${partTypingKey}`}
              layout='position'
              exit={{
                opacity: 0,
                scale: 0.94,
                transition: { duration: 0.18, ease: [0.25, 0.1, 0.25, 1] },
              }}
              transition={{ layout: sameBubbleLayoutTransition }}
            >
              <ChatScenarioAssistantTypingIndicator assistantMessageVariants={assistantMessageVariants} />
            </m.div>
          ) : null}
        </AnimatePresence>
      </div>
    </MessageWithAvatar>
  );
}

export const SameBubbleUserScenarioMessage = memo(SameBubbleUserMessage);
export const SameBubbleAssistantScenarioMessage = memo(SameBubbleAssistantMessage);

export function StaticMessage({
  message,
  assistantMessageVariants,
  userMessageVariants,
}: StaticScenarioMessageProps): JSX.Element {
  return (
    <div className={getMessageAlignmentClassName(message.role)}>
      <MessageWithAvatar avatar={message.avatar} role={message.role}>
        <MessagePartsList
          messageId={message.id}
          role={message.role}
          parts={message.parts}
          assistantMessageVariants={assistantMessageVariants}
          userMessageVariants={userMessageVariants}
        />
      </MessageWithAvatar>
    </div>
  );
}
